import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  ListItem,
  Skeleton,
  Spinner,
  Text,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  CommitWipToSandboxResponseGQLType,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { useCommitCreditAgreementWipToSandboxMutation } from 'capitalProvider/domains/settings/sandbox/requests/requests.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetLenderVerificationCreditFacilitiesQuery } from 'packages/verifications/requests/requests.gql.generated';
import {
  VerificationOptions,
  VerificationOptionsContext,
  VerificationPipelineRunId,
} from 'packages/verifications/ui/VerificationTableSharedComponents';
import { VerificationWorkflow } from 'packages/verifications/ui/VerificationWorkflow';
import { SandboxModeProvider } from '~/contexts/SandboxModeProvider';

// TODO(jon): support non-verification flows in sandbox

type EnsureVerificationFacilityProps = {
  organizationId: string;
  children: React.ReactNode;
  creditFacilityId: string;
};
const EnsureVerificationFacility = ({
  children,
  organizationId,
  creditFacilityId,
}: EnsureVerificationFacilityProps) => {
  const toast = useToast();
  const {
    data: creditFacilitiesData,
  } = useGetLenderVerificationCreditFacilitiesQuery({
    /**
     * Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { organizationId },
  });

  if (!creditFacilitiesData) {
    return <Skeleton h="600px" />;
  }

  if (
    !creditFacilitiesData.lenderVerificationCreditFacilities.some(
      ({ id }) => id === creditFacilityId,
    )
  ) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>
          Sandbox is only supported for verification facilities.
        </AlertDescription>
      </Alert>
    );
  }

  return <>{children}</>;
};

type EnsureSandboxWipCommittedProps = {
  children: React.ReactNode;
  creditFacilityId: string;
};
const EnsureSandboxWipCommitted = ({
  children,
  creditFacilityId,
}: EnsureSandboxWipCommittedProps) => {
  const toast = useToast();
  const [
    commitResult,
    setCommitResult,
  ] = useState<CommitWipToSandboxResponseGQLType>();
  const [
    commitCreditAgreementWipToSandbox,
  ] = useCommitCreditAgreementWipToSandboxMutation({
    /**
     * Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    commitCreditAgreementWipToSandbox().then(({ data }) =>
      setCommitResult(data?.commitCreditAgreementWipToSandbox),
    );
  }, [commitCreditAgreementWipToSandbox]);

  if (!commitResult) {
    return (
      <Flex>
        <Spinner color="green" ml="10px" />
        <Text ml="16px">Initializing sandbox</Text>
      </Flex>
    );
  }

  if (commitResult.errors.length) {
    return (
      <Box>
        <Alert status="error" mb="16px">
          <AlertIcon />
          <AlertDescription>Configuration errors detected</AlertDescription>
        </Alert>
        <Text>Address the following errors:</Text>
        <UnorderedList>
          {commitResult.errors.map((message) => (
            <ListItem key={uuidv4()} ml="10px">
              {message}
            </ListItem>
          ))}
        </UnorderedList>
      </Box>
    );
  }

  return <>{children}</>;
};

type SandboxViewProps = {
  creditFacilityId: string;
};
export const SandboxView = ({ creditFacilityId }: SandboxViewProps) => {
  const toast = useToast();
  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const [
    pipelineRunId,
    setPipelineRunId,
  ] = useState<VerificationPipelineRunId>();

  const options = useMemo<VerificationOptions>(
    () => ({
      navigator: () => ({
        reset: () => window.location.reload(),
        setPipeline: setPipelineRunId,
      }),
      overrides: {
        fixedCreditFacilityId: creditFacilityId,
      },
      displayOptions: {
        showTopLevelHeader: false,
        hideVerification: true,
      },
    }),
    [setPipelineRunId, creditFacilityId],
  );

  if (!userData?.user) {
    return <Skeleton h="600px" />;
  }

  // EnsureSandboxWipCommitted lives outside SandboxMode because it plays a role in setting up the
  // sandbox. It hides sandbox mode components until setup work is complete.
  return (
    <EnsureVerificationFacility
      organizationId={userData.user.organizationId}
      creditFacilityId={creditFacilityId}
    >
      <EnsureSandboxWipCommitted creditFacilityId={creditFacilityId}>
        <SandboxModeProvider>
          <VerificationOptionsContext.Provider value={options}>
            <VerificationWorkflow
              organizationId={userData.user.organizationId}
              pipelineRunId={pipelineRunId}
            />
          </VerificationOptionsContext.Provider>
        </SandboxModeProvider>
      </EnsureSandboxWipCommitted>
    </EnsureVerificationFacility>
  );
};
